/**
 * Retry Utility
 * Rule: RESILIENCE - Read operations should use retry with exponential backoff
 */

/**
 * Execute a function with retry logic
 * @param {Function} fn - Async function to execute
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.baseDelay - Base delay in ms (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 10000)
 * @param {Function} options.shouldRetry - Function to determine if should retry (default: retry on network errors)
 * @returns {Promise<any>}
 */
export async function withRetry(fn, options = {}) {
    const {
        maxRetries = 3,
        baseDelay = 1000,
        maxDelay = 10000,
        shouldRetry = defaultShouldRetry
    } = options;

    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;

            // Check if we should retry
            if (attempt < maxRetries && shouldRetry(error, attempt)) {
                const delay = calculateDelay(attempt, baseDelay, maxDelay);
                await sleep(delay);
                continue;
            }

            throw error;
        }
    }

    throw lastError;
}

/**
 * Default retry condition
 * Retries on network errors and 5xx server errors
 */
function defaultShouldRetry(error, attempt) {
    // Network errors
    if (error.code === 'ECONNREFUSED' || error.code === 'ETIMEDOUT' || error.code === 'ENOTFOUND') {
        return true;
    }

    // Server errors (5xx)
    const statusCode = error.response?.status || error.statusCode;
    if (statusCode >= 500 && statusCode < 600) {
        return true;
    }

    // Rate limiting
    if (statusCode === 429) {
        return true;
    }

    return false;
}

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(attempt, baseDelay, maxDelay) {
    // Exponential backoff: baseDelay * 2^attempt
    const exponentialDelay = baseDelay * Math.pow(2, attempt);

    // Add jitter (random 0-25% of delay)
    const jitter = exponentialDelay * Math.random() * 0.25;

    // Cap at maxDelay
    return Math.min(exponentialDelay + jitter, maxDelay);
}

/**
 * Sleep utility
 */
function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Create a retryable version of an async function
 * @param {Function} fn - Async function
 * @param {Object} options - Retry options
 * @returns {Function} - Wrapped function with retry logic
 */
export function makeRetryable(fn, options = {}) {
    return (...args) => withRetry(() => fn(...args), options);
}
